import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'
export type SidebarState = 'expanded' | 'collapsed'

interface UIState {
  // Sidebar
  sidebarOpen: boolean
  sidebarState: SidebarState
  
  // Theme
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  
  // Mobile
  mobileMenuOpen: boolean
  
  // Notifications
  notificationCount: number

  // Banners
  whatsappHubBannerDismissed: boolean

  // Conversation inbox
  showToolCalls: boolean

  // Actions
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setSidebarState: (state: SidebarState) => void
  setTheme: (theme: Theme) => void
  toggleMobileMenu: () => void
  setMobileMenuOpen: (open: boolean) => void
  setNotificationCount: (count: number) => void
  dismissWhatsAppHubBanner: () => void
  setShowToolCalls: (show: boolean) => void
  initTheme: () => void
}

// Helper to get system theme preference
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

// Helper to apply theme to document
function applyTheme(theme: 'light' | 'dark') {
  const root = document.documentElement
  
  if (theme === 'dark') {
    root.classList.add('dark-mode')
    root.classList.remove('light-mode')
  } else {
    root.classList.add('light-mode')
    root.classList.remove('dark-mode')
  }
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      sidebarState: 'expanded',
      theme: 'system',
      resolvedTheme: 'light',
      mobileMenuOpen: false,
      notificationCount: 0,
      whatsappHubBannerDismissed: false,
      showToolCalls: false,

      // Toggle sidebar open/closed
      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }))
      },

      // Set sidebar open state
      setSidebarOpen: (open) => {
        set({ sidebarOpen: open })
      },

      // Set sidebar expanded/collapsed state
      setSidebarState: (state) => {
        set({ sidebarState: state })
      },

      // Set theme preference
      setTheme: (theme) => {
        const resolved = theme === 'system' ? getSystemTheme() : theme
        applyTheme(resolved)
        set({ theme, resolvedTheme: resolved })
      },

      // Toggle mobile menu
      toggleMobileMenu: () => {
        set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }))
      },

      // Set mobile menu state
      setMobileMenuOpen: (open) => {
        set({ mobileMenuOpen: open })
      },

      // Set notification count
      setNotificationCount: (count) => {
        set({ notificationCount: count })
      },

      // Hide the WhatsApp hub promo bar for good
      dismissWhatsAppHubBanner: () => {
        set({ whatsappHubBannerDismissed: true })
      },

      // Show tool calls in the conversation inbox transcript
      setShowToolCalls: (show) => {
        set({ showToolCalls: show })
      },

      // Initialize theme on app load
      initTheme: () => {
        const { theme } = get()
        const resolved = theme === 'system' ? getSystemTheme() : theme
        applyTheme(resolved)
        set({ resolvedTheme: resolved })

        // Listen for system theme changes
        if (theme === 'system') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
          const handler = (e: MediaQueryListEvent) => {
            const newTheme = e.matches ? 'dark' : 'light'
            applyTheme(newTheme)
            set({ resolvedTheme: newTheme })
          }
          mediaQuery.addEventListener('change', handler)
        }
      },
    }),
    {
      name: 'bonnie-ui',
      // Persist user preferences
      partialize: (state) => ({
        sidebarState: state.sidebarState,
        theme: state.theme,
        whatsappHubBannerDismissed: state.whatsappHubBannerDismissed,
        showToolCalls: state.showToolCalls,
      }),
    }
  )
)

export default useUIStore
